import { Table as MUITable, TableBody, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import { CollectionOrderDirection, DefinitionForType, ResourceType, UnknownAttributes } from 'json-api-hooks';
import React, { JSX, JSXElementConstructor, PropsWithChildren } from 'react';

import Button from '../././Button';
import useModalDispatch, { DispatchProps, ModalContextProps } from '../../hooks/useModalDispatch';
import Card from '../Card';
import Link from '../Link';
import { ModalRenderer } from '../Modal/ModalContextProvider';

import HeadCell from './HeadCell';

type BaseColumn<T extends ResourceType> = {
  attribute: string;
  bold?: boolean;
  label?: string | null | JSX.Element;
  link?: (resource: DefinitionForType<T>) => string | undefined;
  render?: (resource: DefinitionForType<T>) => JSX.Element | string | null | undefined;
  sortable?: boolean;
  width?: string;
  wrap?: boolean;
};

type ColumnWithoutModal<T extends ResourceType> = BaseColumn<T> & {
  modal?: undefined;
  modalOpts?: undefined;
  modalProps?: undefined;
};

type ComponentProps<T> =
  T extends JSXElementConstructor<infer P>
    ? P
    : T extends keyof JSX.IntrinsicElements
      ? JSX.IntrinsicElements[T]
      : void;

type ColumnWithModal<T extends ResourceType, M extends ModalRenderer<any>> = BaseColumn<T> & {
  modal: M;
  modalOpts?: ModalContextProps;
  modalProps: (resource: DefinitionForType<T>) => DispatchProps<ComponentProps<M>>;
};

export type Column<T extends ResourceType, M extends ModalRenderer<any> = ModalRenderer<any>> =
  | ColumnWithoutModal<T>
  | ColumnWithModal<T, M>;

interface TableProps<T extends ResourceType> {
  columns: Column<T>[];
  onRowClick?: (item: DefinitionForType<T>) => void;
  onSortChange: (value: string, direction: CollectionOrderDirection | undefined) => void;
  rows: DefinitionForType<T>[];
  showHead?: boolean;
  sort?: Record<string, CollectionOrderDirection>;
}

const ItemCellWrapper = <T extends ResourceType>({
  children,
  column,
  item,
}: PropsWithChildren<{ column: Column<T>; item: DefinitionForType<T> }>) => {
  const dispatchModal = useModalDispatch(column.modal, column.modalOpts);

  if (column.link) {
    return <Link to={column.link!(item)!}>{children}</Link>;
  }

  if (column.modal) {
    return (
      <Button variant="text" onClick={() => dispatchModal(column.modalProps(item))}>
        {children}
      </Button>
    );
  }

  return children;
};

const ItemCell = <T extends ResourceType>({ column, item }: { column: Column<T>; item: DefinitionForType<T> }) => {
  return (
    <TableCell
      sx={{
        position: 'relative',
        whiteSpace: column.wrap ? 'break-spaces' : 'nowrap',
        width: column.width,
        fontWeight: column.bold ? 'bold' : 'normal',
      }}
    >
      <ItemCellWrapper column={column} item={item}>
        {column.render ? column.render(item) : (item.attributes as UnknownAttributes)[column.attribute]?.toString()}
      </ItemCellWrapper>
    </TableCell>
  );
};

const Table = <T extends ResourceType>({
  columns,
  onRowClick,
  onSortChange,
  rows,
  showHead = true,
  sort,
}: TableProps<T>) => {
  const theme = useTheme();
  const renderer = (item: DefinitionForType<T>) => (
    <TableRow
      key={item.id}
      sx={
        onRowClick
          ? {
              '&:hover': { background: 'gray.lighter', cursor: 'pointer' },
            }
          : undefined
      }
      onClick={onRowClick ? () => onRowClick(item) : undefined}
    >
      {columns.map((col) => {
        return <ItemCell column={col} item={item} key={col.attribute} />;
      })}
    </TableRow>
  );

  return (
    <Card>
      <TableContainer>
        <MUITable size="small">
          {showHead && (
            <TableHead sx={{ backgroundColor: theme.palette.grey.xxLight }}>
              <TableRow>
                {columns.map((col) => {
                  return (
                    <HeadCell<T>
                      key={col.attribute}
                      {...col}
                      currentSorting={sort?.[col.attribute]}
                      onSortChange={onSortChange}
                    />
                  );
                })}
              </TableRow>
            </TableHead>
          )}
          <TableBody>{rows.map((row) => renderer(row))}</TableBody>
        </MUITable>
      </TableContainer>
    </Card>
  );
};

export default Table;
